import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { ArrowRight } from 'lucide-react';
import { worldwideDestinations, worldwideRegions, worldwideContinents } from '../data/mock';

export const WorldwideRegionPage = () => {
  const { continentId, regionId, destinationId } = useParams();
  
  // If destinationId exists, this is actually showing a region (3-level hierarchy)
  // URL: /worldwide-journeys/:continentId/:regionId
  const actualRegionId = regionId || continentId; // Support legacy URLs too
  const region = worldwideRegions.find(r => r.id === actualRegionId);
  
  // Get continent for back navigation
  const continent = region ? worldwideContinents.find(c => c.id === region.continent) : null;

  if (!region) {
    return (
      <div>
        <Navigation />
        <div className="container" style={{ padding: '120px 0', textAlign: 'center' }}>
          <h1 className="hero-large">Region Not Found</h1>
          <Link 
            to="/worldwide-journeys" 
            style={{ color: '#FBBF24', textDecoration: 'none', transition: 'color 0.3s ease' }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#D4AF37'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#FBBF24'}
          >
            ← Back to Worldwide Journeys
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const regionDestinations = worldwideDestinations.filter(d => d.region === actualRegionId);
  
  // Determine back link
  const backLink = continent ? {
    path: `/worldwide-journeys/${continent.id}`,
    label: `← Back to ${continent.name}`
  } : {
    path: '/worldwide-journeys',
    label: '← Back to Worldwide Journeys'
  };

  return (
    <div>
      <Navigation />
      <WhatsAppButton variant="floating" />

      <section style={{
        background: 'var(--bg-secondary)',
        padding: '120px 0 80px',
        textAlign: 'center'
      }}>
        <div className="container">
          <div style={{ marginBottom: '24px' }}>
            <Link 
              to={backLink.path}
              style={{ 
                color: '#FBBF24', 
                textDecoration: 'none',
                fontSize: '14px',
                transition: 'color 0.3s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#D4AF37'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#FBBF24'}
            >
              {backLink.label}
            </Link>
          </div>
          <h1 className="hero-large" style={{ marginBottom: '16px' }}>
            {region.name}
          </h1>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto 32px' }}>
            {region.description}
          </p>
        </div>
      </section>

      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '32px'
          }}>
            {regionDestinations.map((destination) => (
              <Link
                key={destination.id}
                to={`/worldwide-journeys/${destination.continent}/${destination.region}/${destination.slug}`}
                style={{
                  textDecoration: 'none',
                  display: 'block',
                  borderRadius: 0,
                  overflow: 'hidden',
                  background: 'var(--bg-primary)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease'
                }}
                className="destination-card"
              >
                <div style={{
                  width: '100%',
                  height: '220px',
                  overflow: 'hidden'
                }}>
                  <img
                    src={destination.image}
                    alt={destination.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.3s ease'
                    }}
                    className="destination-card-image"
                  />
                </div>
                <div style={{ padding: '24px' }}>
                  <h3 className="heading-2 destination-name" style={{ 
                    marginBottom: '12px',
                    transition: 'color 0.3s ease'
                  }}>
                    {destination.name}
                  </h3>
                  <p className="body-regular" style={{ 
                    color: 'var(--text-secondary)', 
                    marginBottom: '16px',
                    lineHeight: 1.6,
                    minHeight: '48px'
                  }}>
                    {destination.description}
                  </p>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: 'var(--interactive-base)',
                    fontSize: '14px',
                    fontWeight: 400
                  }}>
                    Explore destination
                    <ArrowRight size={16} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        .back-link:hover {
          color: var(--accent-gold) !important;
        }
        .destination-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 12px 24px rgba(0,0,0,0.2);
        }
        .destination-card:hover .destination-card-image {
          transform: scale(1.05);
        }
        .destination-card:hover .destination-name {
          color: var(--accent-gold);
        }
        @media (max-width: 1200px) {
          section .container > div {
            grid-template-columns: repeat(3, 1fr) !important;
          }
        }
        @media (max-width: 768px) {
          section .container > div {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 480px) {
          section .container > div {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};
